import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { flashcardApi, tagApi } from '../utils/api';
import ChessDiagram from '../components/ChessDiagram';

export default function FlashcardViewPage() {
  const { id }          = useParams();
  const { token, prefs } = useAuth();   // prefs already loaded by AuthContext
  const navigate        = useNavigate();

  const [card, setCard]               = useState(null);
  const [showSolution, setShowSolution] = useState(false);
  const [cardTags, setCardTags]       = useState([]);
  const [error, setError]             = useState('');

  useEffect(() => {
    flashcardApi.get(id, token)
      .then(setCard)
      .catch(err => setError(err.message));
    tagApi.listForCard(id, token)
      .then(tags => setCardTags(tags || []))
      .catch(() => {});
  }, [id, token]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this flashcard?')) return;
    try {
      await flashcardApi.delete(id, token);
      navigate('/dashboard');
    } catch (err) {
      alert(err.message);
    }
  };

  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!card)  return <div className="text-center mt-5"><div className="spinner-border" /></div>;

  const showFen     = prefs.showFen !== false;         // default true
  const showTags    = prefs.showTagsOnView !== false;  // default true
  // Card-level flipped overrides the user's diagram preference
  const cardPrefs = { ...prefs, diagramFlipped: card.flipped ?? prefs.diagramFlipped, diagramShowSideToMoveDisc: card.show_side_to_move ?? prefs.diagramShowSideToMoveDisc };

  return (
    <div className="row justify-content-center">
      <div className="col-lg-8">
        {/* Back + actions */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <button className="btn btn-link ps-0" onClick={() => navigate('/dashboard')}>
            ← Back to My Cards
          </button>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary btn-sm"
              onClick={() => navigate(`/flashcards/${id}/edit`)}>
              Edit
            </button>
            <button className="btn btn-outline-danger btn-sm" onClick={handleDelete}>
              Delete
            </button>
          </div>
        </div>

        <div className="card shadow-sm">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="mb-0">{card.name}</h4>
            {card.to_move && (
              <span className={`badge ${card.to_move === 'W' ? 'bg-light text-dark border' : 'bg-dark'}`}>
                {card.to_move === 'W' ? 'White to move' : 'Black to move'}
              </span>
            )}
          </div>

          <div className="card-body">
            {/* ── Chess diagram ─────────────────────────────────────── */}
            {card.fen && (
              <div className="mb-4">
                <div className="d-flex flex-column flex-md-row gap-4 align-items-start">
                  {/* Diagram rendered from saved preferences */}
                  <div className="flex-shrink-0">
                    <ChessDiagram fen={card.fen} prefs={cardPrefs} size={360} />
                  </div>

                  {/* FEN string (visibility controlled by showFen pref) */}
                  {showFen && (
                    <div className="flex-grow-1">
                      <small className="text-muted d-block mb-1 fw-semibold">FEN</small>
                      <code
                        className="user-select-all d-block p-2 bg-light rounded"
                        style={{ wordBreak: 'break-all', fontSize: '0.8rem' }}
                      >
                        {card.fen}
                      </code>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Tags ──────────────────────────────────────────────── */}
            {showTags && cardTags.length > 0 && (
              <div className="mb-4 d-flex flex-wrap gap-1">
                {cardTags.map(t => (
                  <span key={t.id} className="badge rounded-pill bg-secondary">{t.tag}</span>
                ))}
              </div>
            )}

            {/* ── Problem ───────────────────────────────────────────── */}
            {card.problem && (
              <div className="mb-4">
                <h6 className="text-muted text-uppercase small mb-2">Problem</h6>
                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{card.problem}</p>
              </div>
            )}

            {/* ── Solution ──────────────────────────────────────────── */}
            <div>
              <div className="d-flex align-items-center gap-2 mb-2">
                <h6 className="text-muted text-uppercase small mb-0">Solution</h6>
                <button
                  className={`btn btn-sm ${showSolution ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setShowSolution(s => !s)}
                >
                  {showSolution ? 'Hide Solution' : 'Show Solution'}
                </button>
              </div>

              {showSolution ? (
                <div className="solution-box">
                  <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    {card.solution || <em className="text-muted">No solution provided.</em>}
                  </p>
                </div>
              ) : (
                <div className="text-muted fst-italic small">Solution hidden — click to reveal</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
